import React, { useEffect, useState } from 'react';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import { SearchIcon } from 'icons';
import Modal from 'antd/lib/modal';
import Text from 'antd/lib/typography/Text';
import { Col, Row } from 'antd/lib/grid';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import Form from 'antd/lib/form';
import Tooltip from 'antd/lib/tooltip';
import Popover from 'antd/lib/popover';
import Notification from 'antd/lib/notification';
import { t } from 'utils/i18n';
import { fetchAnnotationsAsync, saveAnnotationsAsync } from '../../../../actions/annotation-actions';
import { getCVATStore } from '../../../../cvat-store';
import withVisibilityHandling from './handle-popover-visibility';

export interface Props {
    jobId: number;
}

const CustomPopover = withVisibilityHandling(Popover, 'task-embedding-control');

function TaskLibraryControl(props: Props): React.ReactNode {
    const { jobId } = props;
    const [saving, setSaving] = useState(false);
    const [active, setActive] = useState(false);
    const [libraries, setLibraries] = useState<{ key: string; value: string }[]>([]);
    const [value, setValue] = useState<string[]>([]);
    const baseURL = window.location.origin;
    const storage = getCVATStore();
    // the project folder's room files are listed per task; reference jobs have no task
    const job = storage.getState().annotation.job.instance;
    const taskId = job && 'taskId' in job ? job.taskId : undefined;

    useEffect(() => {
        const fetchLibraries = async () => {
            try {
                const response = await fetch(`${baseURL}/api/get_library${taskId ? `?task_id=${taskId}` : ''}`, {
                    method: 'GET',
                });
                if (!response.ok) {
                    throw new Error(t('Request failed with status {status}', { status: response.status }));
                }
                const data = await response.json();
                setLibraries(data);
            } catch (error) {
                Notification.error({
                    message: t('Error'),
                    description: error.toString(),
                    className: 'cvat-notification-notice-get-issues-error',
                });
            }
        };

        fetchLibraries();
    }, [baseURL, taskId]);

    const handleSearch = async () => {
        if (!value.length) {
            Notification.warning({
                message: t('Select library'),
                description: t('Choose at least one library before starting the search.'),
                className: 'cvat-notification-notice-get-issues-warning',
            });
            return;
        }

        setActive(true);
        await storage.dispatch(
            saveAnnotationsAsync(async () => {
                setSaving(true);
                try {
                    const response = await fetch(
                        `${baseURL}/api/jobs/${jobId}/models?library=${value.map(String)}`,
                        {
                            method: 'GET',
                        },
                    );
                    if (!response.ok) {
                        throw new Error(t('Request failed with status {status}', { status: response.status }));
                    }
                    await storage.dispatch((fetchAnnotationsAsync(true)));
                } catch (error) {
                    console.error('Error:', error);
                    Notification.error({
                        message: t('Error'),
                        description: error.toString(),
                        className: 'cvat-notification-notice-get-issues-error',
                    });
                } finally {
                    setSaving(false);
                    setActive(false);
                }
            }),
        );
    };

    return (
        <>
            <CustomPopover
                overlayClassName='cvat-draw-shape-popover'
                placement='right'
                content={(
                    <div className='cvat-opencv-control-popover-content'>
                        <Row justify='start'>
                            <Text className='cvat-text-color' strong>{t('Select the directory for 3D models search')}</Text>
                        </Row>
                        <br />
                        <Form.Item
                            label={<Text>{t('Select library')}</Text>}
                            rules={[{ required: true, message: t('Directory must be selected') }]}
                        >
                            <Select
                                mode='multiple'
                                value={value}
                                placeholder={t('Select directory')}
                                onChange={(selection: string[]) => setValue(selection)}
                                style={{ width: '350px' }}
                                className='cvat-task-search-field'
                            >
                                {libraries.map((library) => (
                                    <Select.Option value={library.value} title={library.value} key={library.key}>
                                        <Tooltip title={library.key}>{library.key}</Tooltip>
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Row align='middle' justify='end'>
                            <Col>
                                <Button
                                    className='cvat-inference-run-button'
                                    type='primary'
                                    onClick={handleSearch}
                                >
                                    {t('Search')}
                                </Button>
                            </Col>
                        </Row>
                    </div>
                )}
            >
                <Icon
                    className={`cvat-draw-rectangle-control ${active ? 'cvat-active-canvas-control' : 'cvat-disabled-canvas-control'} cvat-embedding-control`}
                    component={SearchIcon}
                    onMouseEnter={() => setActive(true)}
                    onMouseLeave={() => setActive(false)}
                />
            </CustomPopover>
            <Modal
                className='cvat-saving-job-modal'
                title={t('Saving changes on the server')}
                visible={saving}
                footer={null}
                closable={false}
            >
                <Text>{t('Searching for matching 3D models based on your image. Please wait.')}</Text>
                <LoadingOutlined />
            </Modal>
        </>
    );
}

export default React.memo(TaskLibraryControl);
